import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faClipboardCheck, 
  faChevronRight,
  faLock,
  faCalendarAlt,
  faQuestionCircle,
  faClock,
  faCheckCircle
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const TestListGrid = ({ tests, questionsList, profile, isSubscribe, onTestClick, theme, onLockedItemClick }) => {
  const styles = {
    container: {
      background: theme?.background?.card,
      borderRadius: '1.25rem',
      padding: '2rem',
      border: `1px solid ${theme?.borders?.primary}`,
      boxShadow: theme?.shadows?.base,
      marginTop: '2rem',
    },
    header: {
      marginBottom: '1.5rem',
    },
    title: {
      fontSize: '1.25rem',
      fontWeight: '600',
      color: theme?.text?.primary,
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
      gap: '1.5rem',
    },
  };

  return (
    <motion.div
      style={styles.container}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1 }}
    >
      <div style={styles.header}>
        <h2 style={styles.title}>Practice Tests</h2>
      </div>
      
      <div style={styles.grid}>
        {tests.map((test, index) => (
          <TestCard
            key={test.practice_test_id || index}
            test={test}
            questionsList={questionsList}
            profile={profile}
            isSubscribe={isSubscribe}
            onClick={() => onTestClick(test)}
            onLockedItemClick={onLockedItemClick}
            theme={theme}
            index={index}
          />
        ))}
      </div>
    </motion.div>
  );
};

const TestCard = ({ test, questionsList, profile, isSubscribe, onClick, onLockedItemClick, theme, index }) => {
  const [questionCount, setQuestionCount] = useState(0);
  const [testInfo, setTestInfo] = useState({ taken: false, lastDate: null, bestScore: null });

  useEffect(() => {
    // Calculate question count
    const testQuestions = questionsList?.filter(
      q => q.practice_test_id === test.practice_test_id
    ) || [];
    setQuestionCount(testQuestions.length);

    // Get test history
    const testHistory = profile?.practice_test_history?.filter(
      h => h.test_name === test.test_name
    ) || [];

    if (testHistory.length > 0) {
      const dates = testHistory.map(h => moment(h.date, "MMM DD, YYYY HH:mm:ss"));
      const latestDate = moment.max(dates);
      const bestScore = Math.max(...testHistory.map(h => h.score || 0));
      
      setTestInfo({
        taken: true,
        lastDate: latestDate.format("MMM DD, YYYY"),
        bestScore: bestScore,
        attempts: testHistory.length
      });
    }
  }, [test, questionsList, profile]);

  const isLocked = test.requires_subscription && !isSubscribe;

  const getScoreColor = (score) => {
    if (score >= 70) return theme?.primary?.green?.[500];
    if (score >= 50) return theme?.primary?.yellow?.[500];
    return theme?.primary?.red?.[500];
  };

  const styles = {
    card: {
      background: theme?.backgrounds?.secondary,
      borderRadius: '1rem',
      padding: '1.5rem',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      border: `1px solid ${theme?.borders?.primary}`,
      position: 'relative',
      overflow: 'hidden',
      opacity: isLocked ? 0.8 : 1,
    },
    cardHover: {
      transform: 'translateY(-5px)',
      boxShadow: theme?.shadows?.lg,
      borderColor: theme?.borders?.hover,
    },
    lockIconTopRight: {
      position: 'absolute',
      top: '1rem',
      right: '1rem',
      zIndex: 2,
      
      padding: '0.4rem',
      
      color: theme?.primary?.orange?.[500],
      fontSize: '1.25rem',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    header: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '1rem',
      marginBottom: '1rem',
    },
    iconWrapper: {
      width: '3rem',
      height: '3rem',
      borderRadius: '0.75rem',
      background: `linear-gradient(135deg, ${theme?.primary?.blue?.[400]}, ${theme?.primary?.blue?.[600]})`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '1.5rem',
      color: 'white',
      flexShrink: 0,
      position: 'relative',
    },
    lockOverlay: {
      position: 'absolute',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.5)',
      borderRadius: '0.75rem',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    content: {
      flex: 1,
    },
    testName: {
      fontSize: '1.125rem',
      fontWeight: '600',
      color: theme?.text?.primary,
      marginBottom: '0.25rem',
    },
    stats: {
      display: 'flex',
      flexWrap: 'wrap',
      gap: '1rem',
      marginTop: '1rem',
      fontSize: '0.875rem',
      color: theme?.text?.secondary,
    },
    statItem: {
      display: 'flex',
      alignItems: 'center',
      gap: '0.375rem',
    },
    statIcon: {
      color: theme?.text?.secondary,
      fontSize: '0.875rem',
      opacity: 0.7,
    },
    footer: {
      marginTop: '1rem',
      paddingTop: '1rem',
      borderTop: `1px solid ${theme?.borders?.primary}`,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    statusBadge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '0.5rem',
      fontSize: '0.75rem',
      fontWeight: '500',
    },
    arrow: {
      fontSize: '1.25rem',
      color: theme?.text?.tertiary,
      transition: 'all 0.3s ease',
    },
    progressBar: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: theme?.borders?.primary,
    },
    progressFill: {
      height: '100%',
      transition: 'width 0.5s ease',
    },
  };

  return (
    <motion.div
      style={styles.card}
      whileHover={!isLocked ? styles.cardHover : {}}
      onClick={!isLocked ? onClick : onLockedItemClick}
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay: index * 0.05 }}
    >
      {/* Lock icon in top right if locked */}
      {isLocked && (
        <div style={styles.lockIconTopRight}>
          <FontAwesomeIcon icon={faLock} />
        </div>
      )}
      <div style={styles.header}>
        <div style={styles.iconWrapper}>
          <FontAwesomeIcon icon={faClipboardCheck} />
        </div>
        
        <div style={styles.content}>
          <h3 style={styles.testName}>{test.test_name}</h3>
          
          <div style={styles.stats}>
            <div style={styles.statItem}>
              <FontAwesomeIcon icon={faQuestionCircle} style={styles.statIcon} />
              <span>{questionCount} Questions</span>
            </div>
            
            {/* <div style={styles.statItem}>
              <FontAwesomeIcon icon={faClock} style={styles.statIcon} />
              <span>{Math.round(questionCount * 1.5)} mins</span>
            </div> */}
            
            {testInfo.taken && (
              <div style={styles.statItem}>
                <FontAwesomeIcon icon={faCheckCircle} style={styles.statIcon} />
                <span>{testInfo.attempts} attempts</span>
              </div>
            )}
          </div>
        </div>
      </div>
      
      <div style={styles.footer}>
        <div>
          {testInfo.taken ? (
            <div>
              <div style={{
                ...styles.statusBadge,
                background: `${getScoreColor(testInfo.bestScore)}20`,
                color: getScoreColor(testInfo.bestScore),
                marginBottom: '0.5rem',
              }}>
                Best Score: {testInfo.bestScore}%
              </div>
              <div style={styles.statItem}>
                <FontAwesomeIcon icon={faCalendarAlt} style={styles.statIcon} />
                <span style={{ color: theme?.text?.secondary, fontSize: '0.75rem' }}>Last taken: {testInfo.lastDate}</span>
              </div>
            </div>
          ) : (
            <div style={{
              ...styles.statusBadge,
              background: theme?.backgrounds?.tertiary,
              color: theme?.text?.secondary,
            }}>
              Not yet taken
            </div>
          )}
        </div>
        {/* Only show arrow if not locked */}
        {!isLocked && (
          <motion.div
            whileHover={{ x: 5 }}
            style={styles.arrow}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </motion.div>
        )}
      </div>
      
      {testInfo.taken && (
        <div style={styles.progressBar}>
          <div style={{
            ...styles.progressFill,
            width: `${testInfo.bestScore}%`,
            background: `linear-gradient(90deg, ${getScoreColor(testInfo.bestScore)}, ${getScoreColor(testInfo.bestScore)}dd)`,
          }} />
        </div>
      )}
    </motion.div>
  );
};

export default TestListGrid;